import { KSM_ADDON_TYPE, ADDON_CHANGE_OPERATION, ADDON_TYPE } from '@/shared/constants/renewalEstimate.constants';
import { i18n } from '@/i18n';
import { formatCommas } from '@/shared/utils/numbers.util';
import { parseUnitPrice } from '@/shared/utils/tiers.util';
import moment from 'moment';

export default {
    isSelfServiceRevoked: (state) => {
        return state.subscriptionInfo?.revokeSelfService;
    },

    isTrial: (state) => {
        const isTrial = state.subscriptionInfo?.isTrial ?? false;
        return isTrial;
    },

    subscribableContactsTiers: (state) => {
        const tiers = [{ minUnits: 0, maxUnits: 0, unitPrice: 0 }].concat(state.addonsPricing.additionalContacts?.priceTiers ?? []);

        return tiers.map((tier, index) => {
            if (index < tiers.length - 1) {
                return pricingTierToTier(tier);
            }
            return {
                description: `${formatCommas(tier.minUnits)}+ ${i18n.t('edit.subscription.additional')} ${i18n.t('edit.subscription.contacts.name.plural')}`,
                quantity: Number(tier.minUnits),
                price: Number(tier.unitPrice),
            };
        });
    },

    subscribedContactsTierIndex: (state, getters) => {
        let index = 0;
        const lineItem = state.renewalEstimate.estimate.lineItems.find((item) => item.name?.toLowerCase().includes(ADDON_TYPE.ADDITIONAL_CONTACT));
        if (!lineItem) {
            return index;
        }
        if (Number(lineItem.quantity) > getters.subscribableContactsTiers.at(-1).quantity) {
            return getters.subscribableContactsTiers.length - 1;
        }
        index = getters.subscribableContactsTiers.findIndex((tier) => Number(tier.quantity) === Number(lineItem.quantity));
        if (index > 0) {
            return index;
        }
        return 0;
    },

    subscribedLegacyContacts: (state) => {
        const tier = state.renewalEstimate.estimate.lineItems.find((item) => item.name?.toLowerCase().includes(ADDON_TYPE.LEGACY_CONTACT));
        if (tier) {
            return {
                price: Number(tier.price),
                description: `${i18n.t('edit.subscription.legacy.contacts.description', { legacyContactCount: formatCommas(tier.quantity) })}`,
                footer: i18n.t('edit.subscription.promotionalPricingLong'),
                quantity: Number(tier.quantity),
            };
        }
        return {};
    },

    subscribableUsersTiers: (state, getters) => {
        // Prod OOE publishes addon-5-keap-addusers-m2m with an empty tier list, so there is no
        // unit price to build a ladder from. Reading .at(0).unitPrice off that threw; building
        // the ladder from NaN - or from zero, which is an offer - would be worse. Return nothing
        // and let AddonsStep's `length > 0` hide the selector.
        const unitPrice = parseUnitPrice(state.addonsPricing.additionalUsers?.priceTiers?.at(0)?.unitPrice);

        if (unitPrice === null) {
            return [];
        }

        return createTiersFromUnitPrice(unitPrice, state.selectedUsersTier, getters.subscribedUsersTierIndex);
    },

    subscribedUsersTierIndex: (state) => {
        const tier = state.renewalEstimate.estimate.lineItems.find((item) => item.name?.toLowerCase().includes(ADDON_TYPE.ADDITIONAL_USER));
        return tier ? Number(tier.quantity) : 0;
    },

    subscribedLegacyUsers: (state) => {
        const tier = state.renewalEstimate.estimate.lineItems.find((item) => item.name?.toLowerCase().includes(ADDON_TYPE.LEGACY_USER));
        if (tier) {
            return {
                price: Number(tier.price),
                description: `${i18n.tc('edit.subscription.legacy.users.description', Number(tier.quantity), { legacyUserCount: tier.quantity })}`,
                footer: i18n.t('edit.subscription.promotionalPricingLong'),
                quantity: Number(tier.quantity),
            };
        }
        return {};
    },

    subscribableKtvTiers: (state, getters) => {
        const zeroTier = [{
            quantity: 0, price: 0, description: i18n.t('edit.subscription.ktv.tier.description', { minutes: 0, messages: 0 }), minutes: 0, messages: 0,
        }];

        const priceTiers = state.addonsPricing.keapTextAndVoice?.priceTiers ?? [];
        const metadataTiers = state.addonsPricing.keapTextAndVoice?.metadata?.tiers ?? [];

        // The metadata advertises the tiers and the price tiers price them, and the two can
        // disagree - an addon billed on the wrong cadence carries a single flat price and no
        // maxUnits at all, so every lookup here misses. Stop at the first tier we cannot price
        // rather than inventing one: a tier shown at $0 is an offer we would have to honour.
        // Stopping also keeps index === quantity, which subscribedKtvTierIndex relies on.
        const pricedTiers = [];

        for (const tier of metadataTiers) {
            const pricingTier = priceTiers.find((candidate) => candidate.maxUnits === tier.addonQuantity);

            if (!pricingTier) {
                break;
            }

            pricedTiers.push({
                ...pricingTierToTier(pricingTier),
                description: i18n.t('edit.subscription.ktv.tier.description', {
                    minutes: formatCommas(tier.includedMinutes),
                    messages: formatCommas(tier.includedMessages),
                }),
                minutes: tier.includedMinutes,
                messages: tier.includedMessages,
            });
        }

        if (pricedTiers.length === 0) {
            return [];
        }

        const tiers = zeroTier.concat(pricedTiers);

        // If pricing cannot represent the tier the customer currently holds, we cannot describe a
        // change away from it either. Offering the tiers we do have would let them build a change
        // that tierChanges then drops, so offer nothing: AddonsStep's `length > 0` hides the
        // selector and the customer keeps what they have.
        if (getters.subscribedKtvTierIndex >= tiers.length) {
            return [];
        }

        return tiers;
    },

    subscribedKtvTierIndex: (state) => {
        const lineItem = state.renewalEstimate.estimate.lineItems.find((item) => item.name?.toLowerCase().includes(ADDON_TYPE.KTV));
        if (lineItem) {
            return Number(lineItem.quantity);
        }
        return 0;
    },

    subscribableMessageBundleTiers: (state, getters) => {
        const unitPrice = parseUnitPrice(state.addonsPricing.additionalSms?.priceTiers?.at(0)?.unitPrice);

        if (unitPrice === null) {
            return [];
        }

        return createTiersFromUnitPrice(unitPrice, state.selectedMessageBundleTier, getters.subscribedMessageBundleTierIndex)
            .map((tier) => ({
                ...tier,
                description: `${formatCommas(tier.quantity * 500)} ${i18n.t('subscription.details.sms.bundle.description')}`,
            }));
    },

    subscribedMessageBundleTierIndex: (state) => {
        const tier = state.renewalEstimate.estimate.lineItems.find((item) => item.name?.toLowerCase().includes(ADDON_TYPE.MESSAGE_BUNDLE));
        return tier ? Number(tier.quantity) : 0;
    },

    tierChanges: (state, getters) => {
        const changes = [];

        // Every subscribed*TierIndex is a quantity used as an index, and the tier lists are sparse
        // or truncated whenever pricing cannot cover what the customer holds. tierChange keeps the
        // add-on out of the request unless both ends are known: treating a missing tier as
        // quantity 0 would describe a downgrade from tier 5 as "ADD 1".
        changes.push(tierChange(
            KSM_ADDON_TYPE.CONTACT,
            getters.subscribableContactsTiers.at(getters.subscribedContactsTierIndex),
            getters.subscribableContactsTiers.at(state.selectedContactsTier),
        ));

        changes.push(tierChange(
            KSM_ADDON_TYPE.USER,
            { quantity: getters.subscribedUsersTierIndex },
            getters.subscribableUsersTiers.at(state.selectedUsersTier),
        ));

        changes.push(tierChange(
            KSM_ADDON_TYPE.KTV,
            getters.subscribableKtvTiers.at(getters.subscribedKtvTierIndex),
            getters.subscribableKtvTiers.at(state.selectedKtvTier),
        ));

        changes.push(tierChange(
            KSM_ADDON_TYPE.MESSAGE_BUNDLE,
            getters.subscribableMessageBundleTiers.at(getters.subscribedMessageBundleTierIndex),
            getters.subscribableMessageBundleTiers.at(state.selectedMessageBundleTier),
        ));

        return changes.filter(Boolean);
    },

    tierChangesIncludeDowngrade: (state, getters) => {
        const downgradeOperations = [ADDON_CHANGE_OPERATION.REMOVE, ADDON_CHANGE_OPERATION.DECREASE];
        return getters.tierChanges.some((change) => downgradeOperations.includes(change.operation));
    },

    userHasManageBillingPermission: (state) => {
        return state?.hasManageBillingPermission;
    },

    isMonthlySubscription: (state) => {
        return state.subscriptionInfo?.periodUnit?.toLowerCase() === 'month';
    },

    isRecurlySubscription: (state) => {
        // Chooses the Recurly BillingInformation section over the Chargebee portal, and gates
        // loading Recurly billing history/information.
        //
        // Previously inferred from thryvEaid being non-empty, which KSM only populates on some of
        // its Recurly paths - blank for Recurly accounts carrying neither a username nor an EID,
        // and for anything resolved through the UBS read path. That made genuinely Recurly-billed
        // tenants render the Chargebee portal. billingSource is set on every path.
        //
        // KIAB is the one shape where billingSource disagrees with who takes the money: Chargebee
        // is the source of truth for its state while Recurly does the billing, so it reads as
        // Chargebee here. That is fine - KSM holds KIAB self-service unconditionally, so the
        // section this picks is inside a v-if that never renders for them, and returning false
        // also stops two pointless billing fetches. SHAR-6247.
        return state.subscriptionInfo?.billingSource === 'Recurly';
    },

    billingHistory: (state) => {
        return state?.billingHistory;
    },

    nextBillDateText: (state) => {
        if (!state.subscriptionInfo?.nextBillDate) {
            return '';
        }
        const nextBillDateMoment = moment.unix(state.subscriptionInfo.nextBillDate);
        return nextBillDateMoment.format('MMMM D'); // @TODO localize at some point?
    },

    billingInformation: (state) => {
        // For now just return the primary billing info
        return state.billingInformation.find((el) => el.primary);
    },
};

// Returns the change entry for one add-on, or null when there is nothing to send. Both tiers have
// to be present: a tier the pricing payload cannot represent is not a quantity of zero, and
// pretending otherwise sends the wrong operation for a change the customer never asked for.
function tierChange(addonType, subscribedTier, selectedTier) {
    if (!subscribedTier || !selectedTier || subscribedTier.quantity === selectedTier.quantity) {
        return null;
    }

    return {
        addon_type: addonType,
        quantity: selectedTier.quantity,
        operation: calculateChangeOperation(subscribedTier, selectedTier),
    };
}

function calculateChangeOperation(subscribed, selected) {
    if (selected.quantity === 0) {
        return ADDON_CHANGE_OPERATION.REMOVE;
    } else if (subscribed.quantity === 0) {
        return ADDON_CHANGE_OPERATION.ADD;
    } else if (selected.quantity < subscribed.quantity) {
        return ADDON_CHANGE_OPERATION.DECREASE;
    }
    return ADDON_CHANGE_OPERATION.INCREASE;
}

// Defensive for callers that pass the result of a .find() straight in: that misses whenever the
// pricing payload does not cover the tier being asked about, and reading maxUnits off undefined
// throws. A getter is a lazy computed, so the throw surfaces in whichever page reads it - Edit
// Plan, Add Contacts and Add Users all read subscribableKtvTiers, which is why one unpriced text
// & voice tier blanked all three. subscribableKtvTiers no longer reaches here with a miss.
function pricingTierToTier(tier) {
    return {
        quantity: tier?.maxUnits,
        price: tier?.unitPrice,
    };
}

function createTiersFromUnitPrice(unitPrice, selectedTier, subscribedTier) {
    const createTier = (index) => ({ quantity: index, price: index * unitPrice });

    const tiers = selectedTier > 0 ? new Array(selectedTier - 1) : [];
    if (selectedTier > 0) {
        tiers.push(createTier(selectedTier - 1));
    }
    tiers.push(createTier(selectedTier));
    tiers.push(createTier(selectedTier + 1));
    tiers[subscribedTier] = createTier(subscribedTier);
    return tiers;
}
